import { createConnectQueryKey } from "@connectrpc/connect-query";
import type { QueryClient } from "@tanstack/react-query";

import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";

/**
 * Partial connect-query keys used for invalidation after a mutation.
 *
 * `createConnectQueryKey` without `input` and without `transport` produces a
 * key that omits both, and TanStack matches query keys partially, so each of
 * these invalidates every cached call of that RPC regardless of its arguments.
 * That is what we want: after DeleteAccount there is no single ListAccounts
 * input worth being clever about.
 */
export const adminKeys = {
  whoAmI: () =>
    createConnectQueryKey({ schema: AdminService.method.whoAmI, cardinality: "finite" }),
  sessions: () =>
    createConnectQueryKey({ schema: AdminService.method.listSessions, cardinality: "finite" }),
  accounts: () =>
    createConnectQueryKey({ schema: AdminService.method.listAccounts, cardinality: "finite" }),
  account: () =>
    createConnectQueryKey({ schema: AdminService.method.getAccount, cardinality: "finite" }),
  tokens: () =>
    createConnectQueryKey({ schema: AdminService.method.listTokens, cardinality: "finite" }),
  groups: () =>
    createConnectQueryKey({ schema: AdminService.method.listGroups, cardinality: "finite" }),
  batches: () =>
    createConnectQueryKey({ schema: AdminService.method.listBatches, cardinality: "finite" }),
  batch: () =>
    createConnectQueryKey({ schema: AdminService.method.getBatch, cardinality: "finite" }),
  messages: () =>
    createConnectQueryKey({ schema: AdminService.method.listMessages, cardinality: "finite" }),
  webhookDeliveries: () =>
    createConnectQueryKey({
      schema: AdminService.method.listWebhookDeliveries,
      cardinality: "finite",
    }),
  stats: () => createConnectQueryKey({ schema: AdminService.method.getStats, cardinality: "finite" }),
} as const;

/** Anything an account-level mutation can invalidate in one call. */
export function invalidateAccounts(client: QueryClient): Promise<void> {
  return Promise.all([
    client.invalidateQueries({ queryKey: adminKeys.accounts() }),
    client.invalidateQueries({ queryKey: adminKeys.account() }),
  ]).then(() => undefined);
}
